import { useState } from "react";

import {
  ApiError,
  type CreateButtonSetRequest,
  type ParsedButtonSet,
  type PersonalButtonSet,
  type UpdateButtonSetRequest
} from "../../api/client";
import { ButtonLayoutEditor } from "../composer/ButtonLayoutEditor";
import type {
  ButtonLayoutLimits,
  EditableButtonRow
} from "../composer/button-layout-model";

export type ButtonSetEditorApi = {
  parseButtonSet: (source: string) => Promise<ParsedButtonSet>;
  createButtonSet: (body: CreateButtonSetRequest) => Promise<PersonalButtonSet>;
  updateButtonSet: (id: string, body: UpdateButtonSetRequest) => Promise<PersonalButtonSet>;
};

const safeError = (error: unknown) => {
  if (error instanceof ApiError) return error.message;
  if (error && typeof error === "object") {
    const candidate = error as { code?: unknown; message?: unknown };
    if (typeof candidate.code === "string" && typeof candidate.message === "string") {
      return candidate.message;
    }
  }
  return "Не удалось сохранить набор. Попробуйте ещё раз.";
};

export function ButtonRowsPreview({ rows }: { rows: EditableButtonRow[] }) {
  return <div className="button-set-telegram-preview" aria-label="Предпросмотр набора">
    {rows.map((row, rowIndex) => <div role="group" aria-label={`Предпросмотр ряда ${rowIndex + 1}`} key={row.id}>
      {row.buttons.map((button) => <span
        role="button"
        aria-disabled="true"
        aria-label={`Предпросмотр кнопки ${button.text}`}
        className={`telegram-preview-button telegram-preview-button-${button.style}`}
        key={button.id}
      >{button.text}</span>)}
    </div>)}
  </div>;
}

export function ButtonSetEditor({
  api,
  limits,
  item,
  initialMode,
  onCancel,
  onSaved
}: {
  api: ButtonSetEditorApi;
  limits: ButtonLayoutLimits;
  item: PersonalButtonSet | null;
  initialMode: "manual" | "import";
  onCancel: () => void;
  onSaved: (item: PersonalButtonSet) => void;
}) {
  const [mode, setMode] = useState<"manual" | "import">(initialMode);
  const [name, setName] = useState(item?.name ?? "");
  const [rows, setRows] = useState<EditableButtonRow[]>(item?.buttonRows ?? []);
  const [source, setSource] = useState("");
  const [parsed, setParsed] = useState(initialMode === "manual" || Boolean(item));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parse = async () => {
    setBusy(true);
    setError(null);
    setParsed(false);
    try {
      const result = await api.parseButtonSet(source);
      setRows(result.buttonRows);
      setParsed(true);
    } catch (caught) {
      setRows([]);
      setError(safeError(caught));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = item
        ? await api.updateButtonSet(item.id, {
            expectedUpdatedAt: item.updatedAt,
            name: name.trim(),
            buttonRows: rows
          })
        : await api.createButtonSet({
            requestId: crypto.randomUUID(),
            name: name.trim(),
            buttonRows: rows
          });
      onSaved(saved);
    } catch (caught) {
      setError(safeError(caught));
    } finally {
      setBusy(false);
    }
  };

  const canSave = Boolean(name.trim()) && rows.length > 0 && parsed && !busy;

  return <section className="button-set-editor screen-stack" aria-label={item ? `Редактирование набора ${item.name}` : "Новый набор кнопок"}>
    <header className="composer-header">
      <button type="button" aria-label="Назад к наборам" onClick={onCancel}>←</button>
      <div><p className="eyebrow">Личный набор</p><h1>{item ? "Редактирование" : "Новый набор"}</h1></div>
      <span />
    </header>

    {!item && <div className="segmented-control" role="tablist" aria-label="Способ создания набора">
      <button type="button" role="tab" aria-selected={mode === "manual"} onClick={() => { setMode("manual"); setParsed(true); setError(null); }}>Вручную</button>
      <button type="button" role="tab" aria-selected={mode === "import"} onClick={() => { setMode("import"); setParsed(false); setRows([]); setError(null); }}>Вставить списком</button>
    </div>}

    <label className="field"><span>Название набора</span><input aria-label="Название набора" maxLength={80} value={name} onChange={(event) => setName(event.target.value)} placeholder="Например, Продажи" /></label>

    {mode === "import" && !item && <section className="button-set-import field-stack">
      <label className="field"><span>Кнопки из Telegram</span><textarea aria-label="Кнопки из Telegram" rows={10} value={source} onChange={(event) => { setSource(event.target.value); setParsed(false); setRows([]); setError(null); }} placeholder={'Написать менеджеру | @username | primary\nМагазин | https://t.me/shop || AI | @assistant'} /></label>
      <p className="helper-copy">Одна строка — один ряд. Две кнопки в ряду разделяйте через ||. Цвет необязателен: primary, success или danger. Telegram JSON тоже поддерживается.</p>
      <button type="button" disabled={busy || !source.trim()} onClick={() => void parse()}>{busy ? "Проверяем…" : "Проверить кнопки"}</button>
    </section>}

    {error && <p className="inline-error" role="alert">{error}</p>}
    {(mode === "manual" || parsed || item) && <>
      {rows.length > 0 && <ButtonRowsPreview rows={rows} />}
      <ButtonLayoutEditor rows={rows} onChange={setRows} richEnabled limits={limits} />
    </>}

    <footer className="button-set-editor-actions">
      <button type="button" onClick={onCancel}>Отмена</button>
      <button className="primary-button" type="button" disabled={!canSave} onClick={() => void save()}>{busy ? "Сохраняем…" : "Сохранить набор"}</button>
    </footer>
  </section>;
}
